// Add new imports
import { Trans } from '@lingui/macro'
import { Percent } from '@uniswap/sdk-core'
import { OutlineCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import Pagination from 'components/Pagination'
import { MouseoverTooltip } from 'components/Tooltip'
import { useIncentivesQuery } from 'graphql/thegraph/incentives'
import { useMidPosition } from 'hooks/useMidPosition'
import { usePool } from 'hooks/usePools'
import { usePositionAPR } from 'hooks/usePositionAPR'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MoreHorizontal } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { useAPRCalculatorActions, useAPRCalculatorState } from 'state/staking/hooks'
import styled from 'styled-components/macro'
import { Incentive } from 'types/incentives'

import Card from '../../components/Card'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import { RowBetween } from '../../components/Row'
import { Skeleton } from '../../components/Skeleton'
import { useCurrency } from '../../hooks/Tokens'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { ThemedText } from '../../theme/components/text'
import { getIncentiveId, getIncentiveStatus } from '../../utils/incentives'
import ClaimableRewards from './ClaimableRewards'
import LMPositions from './LMPositions'

const StakeV3Wrapper = styled.div`
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: center;
`

const TitleWrapper = styled.div`
  margin-bottom: 1.5rem;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;

  @media screen and (min-width: 992px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 3rem;
  }
`

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;

  @media screen and (min-width: 992px) {
    max-width: 500px;
    margin: 0;
    flex-shrink: 0; /* Prevent shrinking */
  }
`

const ClaimableRewardsWrapper = styled.div`
  width: 100%;

  @media screen and (min-width: 992px) {
    width: 340px;
    margin-left: 0; /* Remove left margin since we're using gap for spacing */
    margin-top: 5.75rem; /* Exact margin value requested for alignment with the incentive list */
    flex-shrink: 0; /* Prevent shrinking */
  }
`

const MobileOnlyView = styled.div`
  display: block;

  @media screen and (min-width: 992px) {
    display: none;
  }
`

const DesktopOnlyView = styled.div`
  display: none;

  @media screen and (min-width: 992px) {
    display: block;
  }
`

const IncentiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const IncentiveButton = styled.button<{ status: 'active' | 'upcoming' | 'expired' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
  background-color: ${({ theme }) => theme.backgroundInteractive};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundModule};
  }
`

const IncentiveLeftContent = styled.div`
  display: flex;
  align-items: center;
`

const IncentiveInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
`

const IncentiveApr = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.accentSuccess};
`

const IncentiveName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const StatusBadge = styled.div<{ status: 'active' | 'upcoming' | 'expired' }>`
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
  width: fit-content;
  background-color: ${({ theme, status }) =>
    status === 'active' ? theme.accentSuccess : status === 'upcoming' ? theme.accentAction : theme.backgroundOutline};
  color: ${({ theme, status }) => (status === 'active' || status === 'upcoming' ? theme.white : theme.textSecondary)};
`

const APRCalculatorCard = styled(Card)`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 1.25rem;
  border-radius: 16px;
  margin-top: 1rem;
`

const CalculatorTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  margin-left: 10px;
`

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${({ theme }) => theme.accentAction};
  }

  &:checked + span:before {
    transform: translateX(16px);
  }
`

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.backgroundOutline};
  transition: 0.4s;
  border-radius: 20px;

  &:before {
    position: absolute;
    content: '';
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: ${({ theme }) => theme.white};
    transition: 0.4s;
    border-radius: 50%;
  }
`

const RangeInputRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0.5rem 0;
`

const RangeInputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const FocusedOutlineCard = styled(OutlineCard)<{ active?: boolean }>`
  border-color: ${({ active, theme }) => active && theme.accentAction};
  padding: 12px;
`

const StyledInput = styled.input`
  color: ${({ theme }) => theme.textPrimary};
  background-color: transparent;
  font-size: 20px;
  font-weight: 500;
  width: 100%;
  text-align: center;
  border: none;
  outline: none;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`

const InputTitle = styled(ThemedText.DeprecatedSmall)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  margin-top: 6px;
`

const OptionsMenuButton = styled.button`
  position: relative;
  width: 36px;
  height: 36px;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }

  svg {
    stroke: ${({ theme }) => theme.textSecondary};
  }
`

const OptionsMenu = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  position: absolute;
  top: 42px;
  right: 0;
  z-index: 10;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  min-width: 20.125rem;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);

  @media screen and (max-width: 768px) {
    min-width: 18.125rem;
    right: -10px;
  }
`

const MoreMenuButton = styled(OptionsMenuButton)`
  // margin-right: 8px;
`

const MoreMenu = styled(OptionsMenu)`
  min-width: 14rem;
`

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundModule};
  }
`

function APRCalculator() {
  const [lowestActive, setLowestActive] = useState(false)
  const [highestActive, setHighestActive] = useState(false)

  // Use Redux state and actions for the APR calculator
  const { lowestPriceChange, highestPriceChange, useCustomRange } = useAPRCalculatorState()
  const { onLowestPriceChangeInput, onHighestPriceChangeInput, onToggleCustomRange } = useAPRCalculatorActions()

  // Store positive number internally but display with minus sign
  const [lowestValue, setLowestValue] = useState(lowestPriceChange.toString())
  const [highestValue, setHighestValue] = useState(highestPriceChange.toString())

  // Update local state when Redux state changes
  useEffect(() => {
    setLowestValue(lowestPriceChange.toString())
  }, [lowestPriceChange])

  useEffect(() => {
    setHighestValue(highestPriceChange.toString())
  }, [highestPriceChange])

  const handleLowestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    // Strip any minus signs the user might enter
    const sanitizedValue = rawValue.replace(/-/g, '')
    setLowestValue(sanitizedValue)
    if (sanitizedValue && !isNaN(parseFloat(sanitizedValue))) {
      // Pass positive value to Redux state
      onLowestPriceChangeInput(parseFloat(sanitizedValue))
    }
  }

  const handleHighestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setHighestValue(value)
    if (value && !isNaN(parseFloat(value))) {
      onHighestPriceChangeInput(parseFloat(value))
    }
  }

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleCustomRange(e.target.checked)
  }

  return (
    <APRCalculatorCard>
      <CalculatorTitle>
        <ThemedText.SubHeaderSmall>
          <Trans>Position APR Calculator</Trans>
        </ThemedText.SubHeaderSmall>
        <ToggleSwitch>
          <ToggleInput type="checkbox" checked={useCustomRange} onChange={handleToggleChange} />
          <ToggleSlider />
        </ToggleSwitch>
      </CalculatorTitle>
      {useCustomRange && (
        <RangeInputRow>
          <RangeInputContainer>
            <FocusedOutlineCard
              active={lowestActive}
              onFocus={() => setLowestActive(true)}
              onBlur={() => setLowestActive(false)}
            >
              <AutoColumn gap="6px">
                <InputTitle fontSize={12}>
                  <Trans>Lowest</Trans>
                </InputTitle>
                {/* Display with minus sign prefix */}
                <StyledInput
                  type="text"
                  placeholder="-10"
                  value={lowestValue ? `-${lowestValue}` : ''}
                  onChange={handleLowestChange}
                />
                <InputTitle fontSize={12}>
                  <Trans>Price change in %</Trans>
                </InputTitle>
              </AutoColumn>
            </FocusedOutlineCard>
          </RangeInputContainer>
          <RangeInputContainer>
            <FocusedOutlineCard
              active={highestActive}
              onFocus={() => setHighestActive(true)}
              onBlur={() => setHighestActive(false)}
            >
              <AutoColumn gap="6px">
                <InputTitle fontSize={12}>
                  <Trans>Highest</Trans>
                </InputTitle>
                <StyledInput type="number" placeholder="10" value={highestValue} onChange={handleHighestChange} />
                <InputTitle fontSize={12}>
                  <Trans>Price change in %</Trans>
                </InputTitle>
              </AutoColumn>
            </FocusedOutlineCard>
          </RangeInputContainer>
        </RangeInputRow>
      )}
      {!useCustomRange && (
        <ThemedText.BodySmall style={{ margin: '16px 0', textAlign: 'center', color: 'textSecondary' }}>
          <Trans>
            Currently using default price ranges for APR calculation. Turn the switch to use a custom range.
          </Trans>
        </ThemedText.BodySmall>
      )}
    </APRCalculatorCard>
  )
}

function IncentiveRow({ incentive }: { incentive: Incentive }) {
  const navigate = useNavigate()
  const status = getIncentiveStatus(incentive)
  const tokenA = useCurrency(incentive.tokenIdA)
  const tokenB = useCurrency(incentive.tokenIdB)

  // Get price change values from Redux
  const { lowestPriceChange, highestPriceChange, useCustomRange } = useAPRCalculatorState()

  // Generate incentive ID for navigation
  const incentiveId = getIncentiveId(incentive.key)

  // Format fee as percentage
  const feePercent = new Percent(incentive.fee, 1_000_000).toSignificant()

  const [, pool] = usePool(tokenA ?? undefined, tokenB ?? undefined, incentive.fee)
  // Use custom range only if toggle is enabled, otherwise pass null for default behavior
  const pos = useMidPosition(
    pool,
    useCustomRange ? lowestPriceChange : null,
    useCustomRange ? highestPriceChange : null
  )
  const apr = usePositionAPR(pos ?? undefined, incentiveId)

  return (
    <IncentiveButton key={incentive.name} status={status} onClick={() => navigate(`/stake/${incentiveId}`)}>
      <IncentiveLeftContent>
        <DoubleCurrencyLogo currency0={tokenA ?? undefined} currency1={tokenB ?? undefined} size={36} margin />
        <IncentiveInfo>
          <IncentiveName>{`${incentive.name} (${feePercent}%)`}</IncentiveName>
          <StatusBadge status={status}>
            <Trans>{status.charAt(0).toUpperCase() + status.slice(1)}</Trans>
          </StatusBadge>
        </IncentiveInfo>
      </IncentiveLeftContent>
      {status === 'expired' ? null : (
        <MouseoverTooltip text="APRs for individual positions may vary depending on the price range set.">
          <IncentiveApr>~{apr}%</IncentiveApr>
        </MouseoverTooltip>
      )}
    </IncentiveButton>
  )
}

function MoreButton() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const node = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useOnClickOutside(node, () => setIsMenuOpen(false))

  const handleCreateIncentive = useCallback(() => {
    navigate('/create-incentive')
    setIsMenuOpen(false)
  }, [navigate])

  return (
    <div ref={node} style={{ position: 'relative' }}>
      <MoreMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="More Options">
        <MoreHorizontal size={20} />
      </MoreMenuButton>
      <MoreMenu isOpen={isMenuOpen}>
        <AutoColumn gap="md" style={{ padding: '0.5rem 0' }}>
          <MenuItem onClick={handleCreateIncentive}>
            <Trans>Create new incentive</Trans>
          </MenuItem>
        </AutoColumn>
      </MoreMenu>
    </div>
  )
}

const PAGE_SIZE = 10
export default function StakeV3() {
  const [currentPage, setCurrentPage] = useState(1)

  // Use pagination with the incentives query
  const { data: incentivesData, loading: incentivesLoading, pageCount } = useIncentivesQuery(PAGE_SIZE, currentPage)

  const filteredIncentives = useMemo(() => {
    if (!incentivesData || incentivesLoading) {
      return []
    }

    return incentivesData // Show all incentives
  }, [incentivesData, incentivesLoading])

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <StakeV3Wrapper>
      <ContentWrapper>
        <ColumnsContainer>
          <MainContentWrapper>
            <TitleWrapper>
              <RowBetween>
                <ThemedText.LargeHeader>
                  <Trans>Stake</Trans>
                </ThemedText.LargeHeader>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <MoreButton />
                </div>
              </RowBetween>
            </TitleWrapper>

            {/* On mobile, ClaimableRewards appears here */}
            <MobileOnlyView>
              <ClaimableRewards />
              <APRCalculator />
              <LMPositions />
            </MobileOnlyView>
            <IncentiveList>
              {incentivesLoading ? (
                // Show skeleton loaders while loading
                [...Array(3)].map((_, index) => (
                  <OutlineCard key={index} style={{ padding: '1rem', marginBottom: '1rem' }}>
                    <Skeleton width="100%" height={60} />
                  </OutlineCard>
                ))
              ) : filteredIncentives.length > 0 ? (
                filteredIncentives.map((incentive, index) => <IncentiveRow key={index} incentive={incentive} />)
              ) : (
                <ThemedText.BodySmall style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Trans>No active incentives found.</Trans>
                </ThemedText.BodySmall>
              )}

              {/* Pagination controls */}
              {pageCount > 1 && !incentivesLoading && filteredIncentives.length > 0 && (
                <Pagination currentPage={currentPage} pageCount={pageCount} onPageChange={handlePageChange} />
              )}
            </IncentiveList>
          </MainContentWrapper>

          {/* On larger screens, ClaimableRewards appears here */}
          <DesktopOnlyView>
            <ClaimableRewardsWrapper>
              <ClaimableRewards />
              <APRCalculator />
              <LMPositions />
            </ClaimableRewardsWrapper>
          </DesktopOnlyView>
        </ColumnsContainer>
      </ContentWrapper>
    </StakeV3Wrapper>
  )
}
